// @ts-strict

import { Inject } from '@angular/core';
import { Injectable } from '@angular/core';
import { WINDOW } from '@app/core/providers/window.provider';
import { NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Subject } from 'rxjs';
import { IDatePickerRanges } from '../../models/core-models';

interface IGeneralChoosedRange {
  selectedRange: string;
  dateFrom: NgbDate;
  dateTo: NgbDate;
  picker: string;
}

@Injectable({
  providedIn: 'root'
})
export class DatePickerService {
  public userRange: {
    selectedRange: string;
    dateFrom: NgbDate;
    dateTo: NgbDate;
    picker: string;
  } = {
    selectedRange: 'user',
    picker: 'general',
    dateTo: this.calendar.getToday(),
    dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'm', 1)
  };

  public currentRange = new BehaviorSubject<string>('');
  public currentRangeObs = this.currentRange.asObservable();
  public generalChoosedRange = new BehaviorSubject<IGeneralChoosedRange>(this.setInitialDatePickerDate());

  public compareChoosedRange = new BehaviorSubject('');
  public selectedDateRangeForWidget = new BehaviorSubject<IDatePickerRanges>({
    range: 'daily',
    // translation: 'view_translations.daily',
    translation: 'daily',
    dateFrom: this.calendar.getToday(),
    dateTo: this.calendar.getToday(),
    active: true
  });

  public statisticsDateRange = new Subject();
  public dateRanges: Array<IDatePickerRanges> = [];
  public dateRangesForWidget: Array<IDatePickerRanges> = [
    {
      range: 'daily',
      translation: 'daily',
      dateFrom: this.calendar.getToday(),
      dateTo: this.calendar.getToday(),
      active: true
    },
    {
      range: 'weekly',
      translation: 'weekly',
      dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'd', 6),
      dateTo: this.calendar.getToday(),
      active: false
    },
    {
      range: 'monthly',
      translation: 'monthly',
      dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'm', 1),
      dateTo: this.calendar.getToday(),
      active: false
    },
    {
      range: 'custom',
      translation: 'period_user',
      // dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'd', 6),
      // dateTo: this.calendar.getToday(),
      dateFrom: !!sessionStorage.getItem('custom_statistics_user_selected_date_range')
        ? JSON.parse(sessionStorage.getItem('custom_statistics_user_selected_date_range')!).dateFrom
        : this.calendar.getPrev(this.calendar.getToday(), 'd', 6),
      dateTo: !!sessionStorage.getItem('custom_statistics_user_selected_date_range')
        ? JSON.parse(sessionStorage.getItem('custom_statistics_user_selected_date_range')!).dateTo
        : this.calendar.getToday(),
      active: false
    }
  ];

  constructor(
    private readonly calendar: NgbCalendar,
    @Inject(WINDOW) private window: Window
  ) {}

  public setCurrentRange(range: string): void {
    this.currentRange.next(range);
  }

  public getDateRanges(): Array<IDatePickerRanges> {
    const today = this.calendar.getToday();
    const previousMonthStart = this.calendar.getPrev(today, 'm', 1);
    const previousWeekStart = this.calendar.getPrev(today, 'd', 7);
    const lastYear = this.calendar.getPrev(today, 'y', 1);
    const previousWeekDay = this.calendar.getWeekday(previousWeekStart) - 1;
    const previousMonthLength = this.getNumberOfDaysMonth(today.month - 1, today.year) - 1;
    this.dateRanges = [
      {
        range: 'today',
        dateFrom: this.calendar.getToday(),
        dateTo: this.calendar.getToday()
      },
      {
        range: 'yesterday',
        dateFrom: this.calendar.getPrev(today, 'd', 1),
        dateTo: this.calendar.getPrev(today, 'd', 1)
      },
      {
        range: 'last_week',
        dateFrom: this.calendar.getPrev(previousWeekStart, 'd', previousWeekDay),
        dateTo: this.calendar.getNext(this.calendar.getPrev(previousWeekStart, 'd', previousWeekDay), 'd', 6)
      },
      {
        range: 'current_week',
        dateFrom: this.calendar.getPrev(today, 'd', this.calendar.getWeekday(today) - 1),
        dateTo: today
      },
      {
        range: 'last_7_days',
        dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'd', 6),
        dateTo: today
      },
      {
        range: 'last_month',
        dateFrom: this.calendar.getPrev(previousMonthStart, 'd', today.day - 1),
        dateTo: this.calendar.getNext(this.calendar.getPrev(previousMonthStart, 'd', today.day - 1), 'd', previousMonthLength)
      },
      {
        range: 'current_month',
        dateFrom: this.calendar.getPrev(today, 'd', today.day - 1),
        dateTo: today
      },
      {
        range: 'this_year',
        dateFrom: new NgbDate(today.year, 1, 1),
        dateTo: today
      },
      {
        range: 'last_year',
        dateFrom: new NgbDate(lastYear.year, 1, 1),
        dateTo: new NgbDate(lastYear.year, 12, 31)
      }
    ];

    return this.dateRanges;
  }

  public getNumberOfDaysMonth(month: number, year: number): number {
    const monthLength = [31, year % 4 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return monthLength[(month === 0 ? 12 : month) - 1];
  }

  public setChoosedRange(date: { selectedRange: string; dateFrom: NgbDate; dateTo: NgbDate; picker: string }): void {
    if (date.selectedRange === 'user') {
      this.userRange = date;
    }

    this.generalChoosedRange.next(date);
  }

  public stringifyNgbDate(ngbDate: NgbDate): string {
    // return ngbDate.year + '-' + ngbDate.month + '-' + ngbDate.day;
    const month = ngbDate.month.toString().padStart(2, '0');
    const day = ngbDate.day.toString().padStart(2, '0');

    return `${ngbDate.year}-${month}-${day}`;
  }

  public formatDate = (date: { day: number; month: number; year: number; weekday?: string }): string => {
    const day = date.day < 10 ? '0' + date.day : date.day;
    const month = date.month < 10 ? '0' + date.month : date.month;
    const year = date.year;

    return `${day}/${month}/${year}`;
  };

  public setDefaultDateRange(): void {
    let initialDate = {
      dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'd', 30),
      dateTo: this.calendar.getToday(),
      picker: 'general',
      selectedRange: 'last_30_days'
    };
    sessionStorage.setItem('general_statistics_user_selected_date_range', JSON.stringify({ dateFrom: initialDate.dateFrom, dateTo: initialDate.dateTo }));
    sessionStorage.setItem('general_statistics_date_range', initialDate.selectedRange);

    this.generalChoosedRange.next(initialDate);
  }

  private setInitialDatePickerDate(): IGeneralChoosedRange {
    let initialDate = {
      dateFrom: this.calendar.getPrev(this.calendar.getToday(), 'd', 30),
      dateTo: this.calendar.getToday(),
      picker: 'general',
      selectedRange: 'last_30_days'
    };

    const initialDateFromSessionStorage = this.window.sessionStorage.getItem('general_statistics_user_selected_date_range');
    const initialDateRangeFromSessionStorage = this.window.sessionStorage.getItem('general_statistics_date_range');

    if (initialDateFromSessionStorage && initialDateRangeFromSessionStorage) {
      initialDate = { ...initialDate, ...JSON.parse(initialDateFromSessionStorage), selectedRange: initialDateRangeFromSessionStorage };
    }
    return initialDate;
  }
}
